import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { Search, X } from "lucide-react";

import { AutocompleteDropdown } from "@/features/shop/components/SearchBarAutocomplete/AutocompleteDropdown";
import { useAutocompleteSearch } from "@/features/shop/hooks/useAutocompleteSearch";
import type { AutocompleteResult, SearchBarAutocompleteProps } from "@/features/shop/types/autocomplete.types";

const DEFAULT_PLACEHOLDER = "Disneyland Paris, Amazon, Fnac, Netflix, Parc Astérix...";

export const SearchBarAutocomplete = ({
  placeholder = DEFAULT_PLACEHOLDER,
  onSearch,
  initialValue = "",
  onValueChange,
}: SearchBarAutocompleteProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const { results, isLoading, isQueryValid } = useAutocompleteSearch(query);
  const hasQuery = query.length > 0;

  const handleSelect = useCallback((result: AutocompleteResult) => {
    setQuery(result.name);
    setIsOpen(false);
    setHighlightedIndex(-1);
    navigate(result.link);
  }, [navigate]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setHighlightedIndex(-1);
    onValueChange?.(value);
    if (value.length > 0) {
      setIsOpen(true);
    }
    else {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    onValueChange?.("");
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (hasQuery) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < results.length) {
        handleSelect(results[highlightedIndex]);
      }
      else if (onSearch && query.trim().length > 0) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        onSearch(query.trim());
      }
      return;
    }

    if (!isOpen || results.length === 0) {
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1,
        );
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative z-10 w-full">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-4">
          <Search size={16} className="text-gray-400 dark:text-gray-500" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="relative z-10 w-full rounded-xl border-0 bg-white py-4 pl-12 pr-10 text-base font-medium text-gray-900 shadow-lg transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white/30 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:ring-white/20"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        {hasQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 z-20 flex items-center pr-4 text-gray-400 transition hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Effacer la recherche"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <AutocompleteDropdown
        results={results}
        isOpen={isOpen}
        isLoading={isLoading}
        hasQuery={hasQuery}
        isQueryValid={isQueryValid}
        highlightedIndex={highlightedIndex}
        onSelect={handleSelect}
        onHighlight={setHighlightedIndex}
      />
    </div>
  );
};
